import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Download, Mail } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadManager = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/profiles/me/leads`, {
        withCredentials: true
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const exportToCSV = () => {
    if (leads.length === 0) return;

    const headers = ['Name', 'Email', 'Note', 'Date'];
    const rows = leads.map(lead => [
      lead.name,
      lead.email,
      lead.note || '',
      format(new Date(lead.created_at), 'MMM dd, yyyy')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-[#B8935A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="lead-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0A0A0A]">Lead Manager</h1>
          <p className="text-[#525252] mt-1">{leads.length} total leads captured</p>
        </div>
        {leads.length > 0 && (
          <Button
            onClick={exportToCSV}
            className="bg-[#0A0A0A] rounded-full"
            data-testid="export-csv-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        )}
      </div>

      {leads.length === 0 ? (
        <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-12 text-center" data-testid="no-leads-message">
          <Mail className="w-16 h-16 text-[#525252] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#0A0A0A] mb-2">No leads yet</h3>
          <p className="text-[#525252]">
            When people submit the contact form on your profile, they'll appear here.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#0A0A0A]/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="leads-table">
              <thead>
                <tr className="border-b border-[#0A0A0A]/10">
                  <th className="text-left px-6 py-4 text-sm font-medium text-[#525252]">Name</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-[#525252]">Email</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-[#525252]">Note</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-[#525252]">Date</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.lead_id} className="border-b border-[#0A0A0A]/10 hover:bg-[#F5F1EA] transition-all" data-testid={`lead-row-${lead.lead_id}`}>
                    <td className="px-6 py-4 text-[#0A0A0A] font-medium">{lead.name}</td>
                    <td className="px-6 py-4 text-[#525252]">
                      <a href={`mailto:${lead.email}`} className="hover:text-[#B8935A] transition-all">
                        {lead.email}
                      </a>
                    </td>
                    <td className="px-6 py-4 text-[#525252] max-w-md truncate">
                      {lead.note || '-'}
                    </td>
                    <td className="px-6 py-4 text-[#525252]">
                      {format(new Date(lead.created_at), 'MMM dd, yyyy')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadManager;
